import { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { isAdmin as isAdminByEmail } from '@/lib/adminAuth';
import { avatarFor } from '@/lib/defaultAvatar';
import {
  X, Home, Compass, PlusCircle, TrendingUp, User, Bell, Users,
  Settings, HelpCircle, Info, LogOut, LogIn, Shield, Megaphone,
} from 'lucide-react';

/**
 * Web-only side drawer that surfaces every top-level destination plus the
 * Profile/Settings/Sign-out chrome that lives in the mobile bottom-nav on
 * native builds.  Rendered behind a Capacitor-aware gate by `Nav` — never
 * shipped to the iOS/Android binaries.
 */
const NAV_ITEMS = [
  { label: 'Home',          to: '/',              icon: Home },
  { label: 'Zenflow',       to: '/zenflow',       icon: Compass },
  // "Following" — the dedicated Following feed (creator-only timeline).
  // Lives at /following (Zenflow with ?tab=following).  Same destination
  // as the top-bar Following link in LandingShell so the two nav surfaces
  // stay in sync.
  { label: 'Following',     to: '/following',     icon: Users,       requiresAuth: true },
  { label: 'Create',        to: '/create',        icon: PlusCircle,  requiresAuth: true },
  { label: 'Trending',      to: '/trending',      icon: TrendingUp },
];

const SECONDARY_ITEMS = [
  { label: 'Profile',       to: '/profile/me',    icon: User,        requiresAuth: true },
  { label: 'Notifications', to: '/notifications', icon: Bell,        requiresAuth: true },
  { label: 'Settings',      to: '/settings',      icon: Settings,    requiresAuth: true },
];

const FOOTER_ITEMS = [
  { label: 'About',         to: '/about',         icon: Info },
  { label: 'Help',          to: '/help',          icon: HelpCircle },
];

// Single navigation row.  Lifted to module scope so it isn't redefined on
// every WebSideMenu render — previously each parent re-render replaced the
// component identity, which made React unmount and remount every row on
// every menu open / pathname change.
// `go` and `pathname` are passed in as props; the menu owns navigation.
function Row({ item, active = false, go, pathname }) {
  const Icon = item.icon;
  const isCurrent = active || (item.to && pathname === item.to);
  return (
    <button
      data-testid={`websidemenu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={() => go(item.to)}
      className={`group relative w-full flex items-center gap-3 px-5 py-2.5 rounded-xl text-left transition-all duration-200 ${
        isCurrent
          ? 'bg-gradient-to-r from-[#1C4749]/30 to-transparent text-white'
          : 'text-white/80 hover:text-white hover:bg-white/[0.04]'
      }`}
    >
      <span
        className={`absolute left-1.5 top-1/2 -translate-y-1/2 w-[3px] rounded-full transition-all duration-200 ${
          isCurrent
            ? 'h-6 bg-[#5EEAD4] shadow-[0_0_12px_rgba(94,234,212,0.6)]'
            : 'h-0 bg-transparent group-hover:h-4 group-hover:bg-[#5EEAD4]/40'
        }`}
      />
      <Icon className={`w-4 h-4 transition-colors ${isCurrent ? 'text-[#5EEAD4]' : 'text-[#5EEAD4]/70 group-hover:text-[#5EEAD4]'}`} />
      <span className="text-sm font-medium tracking-wide">{item.label}</span>
    </button>
  );
}

function SectionLabel({ children }) {
  return (
    <p className="px-5 pt-3 pb-1.5 text-[10px] uppercase tracking-[0.22em] text-[#5EEAD4]/60 font-semibold">{children}</p>
  );
}

export default function WebSideMenu({ open, onClose, token, onLogout }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const cachedUser = (() => {
    try { return JSON.parse(localStorage.getItem('zenscroll_user') || 'null'); }
    catch { return null; }
  })();
  // Admin access is granted when ANY of these is true:
  //   1. cached user has `role === 'admin'` (server-promoted)
  //   2. cached user has `is_admin === true` (legacy flag)
  //   3. cached user's email is on the `ADMIN_EMAILS` allowlist (the
  //      frontend mirror of the backend ADMIN_EMAILS set in server.py)
  // This lets allow-listed admins (e.g. richard@richailabs.com) see the
  // admin menu without needing the user document mutated server-side.
  const isAdmin = !!(
    cachedUser && (cachedUser.is_admin || cachedUser.role === 'admin')
  ) || isAdminByEmail();

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const go = (to) => {
    onClose();
    // Profile -> use the cached username when we have it
    if (to === '/profile/me' && cachedUser?.username) {
      navigate(`/profile/${cachedUser.username}`);
    } else {
      navigate(to);
    }
  };

  /** Sign out without needing the parent to thread `onLogout` from every page.
   * Calls the App-level handler when provided, otherwise mirrors its effect
   * by clearing the cached token + user and dispatching the mood-survey hook
   * the rest of the app expects on logout. */
  const handleSignOut = () => {
    onClose();
    if (typeof onLogout === 'function') {
      onLogout();
      return;
    }
    const proceed = () => {
      localStorage.removeItem('zenscroll_token');
      localStorage.removeItem('zenscroll_onboarded');
      localStorage.removeItem('zenscroll_user');
      // Hard reload so the app re-reads the (now missing) token and lands on /auth.
      window.location.assign('/');
    };
    window.dispatchEvent(new CustomEvent('zenscroll:mood-survey', { detail: { onDone: proceed } }));
    // Fail-safe — if no listener exists the mood event is a no-op, so still log out.
    setTimeout(() => {
      if (localStorage.getItem('zenscroll_token')) proceed();
    }, 400);
  };

  // Memoized filtered nav items — avoids re-running the filter on every
  // re-render (and stops the .map() below from creating new Row instances
  // when the inputs haven't changed).
  const visibleNavItems = useMemo(
    () => NAV_ITEMS.filter((it) => !it.requiresAuth || token),
    [token],
  );

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop — deeper blur + warm-tinted scrim for depth */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[95] bg-[#0A1514]/75 backdrop-blur-md"
            data-testid="websidemenu-backdrop"
          />
          {/* Drawer — layered gradient surface with a soft teal halo on the
              inside edge so the menu reads as a premium surface rather than
              a flat panel.  Two-stop gradient deepens toward the right
              edge for subtle depth; the leading 1px-wide teal stripe gives
              the drawer a clear "anchor" to the brand. */}
          <motion.aside
            initial={{ x: '100%', opacity: 0.6 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0.6 }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="fixed top-0 right-0 bottom-0 z-[96] w-[88vw] max-w-sm h-screen flex flex-col overflow-hidden border-l border-white/10 shadow-[-30px_0_80px_rgba(0,0,0,0.55)] bg-gradient-to-b from-[#101F1D] via-[#0B1124] to-[#0A1514]"
            data-testid="websidemenu-drawer"
            style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
          >
            {/* Layered inner glow — accent the top-left and bottom-right
                corners of the drawer for premium depth without affecting
                layout. `pointer-events-none` so taps still hit the
                underlying nav rows. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 opacity-70"
              style={{
                background:
                  'radial-gradient(120% 50% at 0% 0%, rgba(28,71,73,0.32) 0%, rgba(28,71,73,0) 55%), radial-gradient(80% 40% at 100% 100%, rgba(94,234,212,0.08) 0%, rgba(94,234,212,0) 60%)',
              }}
            />
            {/* Leading teal accent stripe — 1px brand anchor on the left edge */}
            <div aria-hidden="true" className="pointer-events-none absolute top-0 bottom-0 left-0 w-px bg-gradient-to-b from-transparent via-[#5EEAD4]/50 to-transparent" />

            {/* Header — brand-forward instead of a tiny "Menu" eyebrow */}
            <div className="relative flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#5EEAD4]/70 font-semibold mb-0.5">ZenScroll</p>
                <p className="text-base text-white font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Menu</p>
              </div>
              <button
                onClick={onClose}
                aria-label="Close menu"
                data-testid="websidemenu-close"
                className="w-10 h-10 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.10] hover:border-[#5EEAD4]/30 flex items-center justify-center transition-all"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            {token && cachedUser && (
              <button
                data-testid="websidemenu-profile-card"
                onClick={() => go('/profile/me')}
                className="relative mx-3 mt-4 mb-1 flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-gradient-to-br from-[#1C4749]/25 via-white/[0.03] to-transparent border border-[#5EEAD4]/15 hover:border-[#5EEAD4]/30 hover:from-[#1C4749]/35 transition-all text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
              >
                <div className="w-11 h-11 rounded-full overflow-hidden bg-gradient-to-br from-[#1C4749] to-[#2A6F72] flex items-center justify-center text-white text-base font-semibold ring-2 ring-[#5EEAD4]/20 shadow-[0_0_18px_rgba(94,234,212,0.25)]">
                  <img src={avatarFor(cachedUser)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold truncate">{cachedUser.display_name || cachedUser.username}</p>
                  {cachedUser.username && (
                    <p className="text-[#94A3B8] text-xs truncate">@{cachedUser.username}</p>
                  )}
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[#5EEAD4]/70 font-semibold">View</span>
              </button>
            )}

            <div className="relative flex-1 overflow-y-auto px-2 py-2 space-y-0.5 scrollbar-hide">
              <SectionLabel>Explore</SectionLabel>
              {visibleNavItems.map((it) => <Row key={it.label} item={it} go={go} pathname={pathname} />)}

              {token && (
                <>
                  <SectionLabel>Account</SectionLabel>
                  {SECONDARY_ITEMS.map((it) => <Row key={it.label} item={it} go={go} pathname={pathname} />)}
                </>
              )}

              {isAdmin && (
                <>
                  <SectionLabel>Admin</SectionLabel>
                  <Row item={{ label: 'Admin Dashboard',  to: '/admin',             icon: Shield }} go={go} pathname={pathname} />
                  <Row item={{ label: 'Moderation',       to: '/admin/moderation',  icon: Shield }} go={go} pathname={pathname} />
                  <Row item={{ label: 'Ads',              to: '/admin/ads',         icon: Megaphone }} go={go} pathname={pathname} />
                  <Row item={{ label: 'AI Settings',      to: '/admin/ai-settings', icon: Settings }} go={go} pathname={pathname} />
                </>
              )}

              <SectionLabel>More</SectionLabel>
              {FOOTER_ITEMS.map((it) => <Row key={it.label} item={it} go={go} pathname={pathname} />)}
            </div>

            <div className="relative border-t border-white/[0.06] p-3 space-y-2 bg-gradient-to-t from-[#0A1514]/80 to-transparent">
              {token ? (
                <button
                  data-testid="websidemenu-signout"
                  onClick={handleSignOut}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.10] hover:border-[#5EEAD4]/25 text-white text-sm font-medium transition-all"
                >
                  <LogOut className="w-4 h-4" /> Sign Out
                </button>
              ) : (
                <button
                  data-testid="websidemenu-signin"
                  onClick={() => go('/auth')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#1C4749] hover:bg-[#2A6F72] text-white text-sm font-semibold transition-colors shadow-[0_0_24px_rgba(28,71,73,0.5)]"
                >
                  <LogIn className="w-4 h-4" /> Sign In
                </button>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
